import React from 'react';
import { Link } from 'react-router-dom';
import { categories, compounds, getCompoundsByCategory } from '../data/compounds';
import './CategoriesIndex.css';

function CategoryCard({ category }) {
  const catCompounds = getCompoundsByCategory(category.id);
  const avgBenefit = catCompounds.length
    ? Math.round(catCompounds.reduce((s, c) => s + c.benefitScore, 0) / catCompounds.length)
    : 0;
  const avgHarm = catCompounds.length
    ? Math.round(catCompounds.reduce((s, c) => s + c.harmScore, 0) / catCompounds.length)
    : 0;
  const highEvidence = catCompounds.filter(c => c.evidenceConfidence === 'HIGH').length;
  const unscheduled = catCompounds.filter(c => c.schedulingStatus === 'unscheduled').length;

  return (
    <Link
      to={`/categories/${category.id}`}
      className="cat-card"
      style={{
        '--cat-color': category.color,
        '--cat-color-light': category.colorLight,
        '--cat-border': category.colorBorder,
      }}
    >
      <div className="cat-card-accent" />
      <div className="cat-card-header">
        <div className="cat-card-icon">{category.icon}</div>
        <div className="cat-card-title-block">
          <h3 className="cat-card-name">{category.name}</h3>
          <span className="cat-compound-count">{catCompounds.length} compound{catCompounds.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <p className="cat-card-desc">{category.description}</p>

      <div className="cat-card-stats">
        <div className="cat-stat">
          <span className="cat-stat-value benefit">{avgBenefit}</span>
          <span className="cat-stat-label">Avg Benefit</span>
        </div>
        <div className="cat-stat-divider" />
        <div className="cat-stat">
          <span className="cat-stat-value harm">{avgHarm}</span>
          <span className="cat-stat-label">Avg Risk</span>
        </div>
        <div className="cat-stat-divider" />
        <div className="cat-stat">
          <span className="cat-stat-value">{highEvidence}</span>
          <span className="cat-stat-label">High Evidence</span>
        </div>
        <div className="cat-stat-divider" />
        <div className="cat-stat">
          <span className="cat-stat-value unscheduled">{unscheduled}</span>
          <span className="cat-stat-label">Unscheduled</span>
        </div>
      </div>

      <div className="cat-card-compounds">
        {catCompounds.slice(0, 4).map(c => (
          <span key={c.id} className="cat-compound-pill">{c.name}</span>
        ))}
        {catCompounds.length > 4 && (
          <span className="cat-compound-pill more">+{catCompounds.length - 4} more</span>
        )}
      </div>

      <div className="cat-card-cta">
        <span>View Category</span>
        <span className="cta-arrow">→</span>
      </div>
    </Link>
  );
}

export default function CategoriesIndex() {
  const totalCompounds = Object.keys(compounds).length;
  const totalUnscheduled = Object.values(compounds).filter(c => c.schedulingStatus === 'unscheduled').length;

  return (
    <div className="page-content categories-index">
      {/* Page Header */}
      <div className="ci-hero">
        <div className="container">
          <nav className="ci-breadcrumb">
            <Link to="/">Home</Link>
            <span className="bc-sep">›</span>
            <span>Categories</span>
          </nav>
          <h1 className="ci-title">Research Categories</h1>
          <p className="ci-subtitle">
            Explore {totalCompounds} research compounds organized across {categories.length} therapeutic categories.
            Each category represents a distinct pharmacological mechanism and clinical application area.
          </p>
          <div className="ci-hero-stats">
            <div className="ci-hero-stat">
              <span className="ci-hero-stat-value">{categories.length}</span>
              <span className="ci-hero-stat-label">Categories</span>
            </div>
            <div className="ci-hero-stat">
              <span className="ci-hero-stat-value">{totalCompounds}</span>
              <span className="ci-hero-stat-label">Total Compounds</span>
            </div>
            <div className="ci-hero-stat">
              <span className="ci-hero-stat-value">{totalUnscheduled}</span>
              <span className="ci-hero-stat-label">Unscheduled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="ci-body">
        <div className="container">
          <div className="ci-grid">
            {categories.map(cat => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>

          {/* Legend */}
          <div className="ci-legend">
            <h3 className="legend-title">Scoring Legend</h3>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-dot benefit" />
                <div>
                  <span className="legend-label">Benefit Score</span>
                  <span className="legend-desc">Research utility & therapeutic potential (0–100)</span>
                </div>
              </div>
              <div className="legend-item">
                <div className="legend-dot harm" />
                <div>
                  <span className="legend-label">Risk Score</span>
                  <span className="legend-desc">Safety & adverse event profile (0–100, lower is better)</span>
                </div>
              </div>
              <div className="legend-item">
                <div className="legend-dot evidence" />
                <div>
                  <span className="legend-label">Evidence Confidence</span>
                  <span className="legend-desc">Quality and quantity of peer-reviewed literature</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}